import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canWrite } from '../../rbac';
import { contentAPI } from '../../services/api';
import useBackButtonClose from '../../hooks/useBackButtonClose';
import { formatMarathiDate } from '../../utils/marathi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './AdminNotices.css';

const apiErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  if (Array.isArray(data?.details) && data.details.length) {
    return data.details.map(item => item.message).join(', ');
  }
  return data?.error || fallback;
};

const E_DAWANDI_CATEGORIES = [
  { value: 'निधन वार्ता / शोक संदेश', label: '🕊️ निधन वार्ता / शोक संदेश' },
  { value: 'पाणीपुरवठा', label: '💧 पाणीपुरवठा सूचना' },
  { value: 'वीजपुरवठा', label: '⚡ वीजपुरवठा सूचना' },
  { value: 'आरोग्य / लसीकरण', label: '🏥 आरोग्य / लसीकरण शिबिर' },
  { value: 'आपत्कालीन', label: '🚨 आपत्कालीन सूचना' },
  { value: 'इतर', label: '📢 इतर महत्त्वाची सूचना' },
];

const buildNoticePayload = formData => ({
  title: formData.title.trim(),
  content: formData.content.trim(),
  category: formData.category || 'general',
  sub_category: formData.is_e_dawandi ? (formData.sub_category || 'इतर') : null,
  priority: formData.priority || 'normal',
  publish_date: formData.publish_date || null,
  expiry_date: formData.expiry_date || null,
  is_published: formData.is_published !== false,
  attachment_url: formData.attachment_url || null,
  is_e_dawandi: Boolean(formData.is_e_dawandi),
  broadcast_whatsapp: Boolean(formData.broadcast_whatsapp),
});

export default function AdminNotices() {
  const { tenant, user } = useAuth();
  const slug = tenant?.slug;
  const canWriteThis = canWrite(user?.role, 'notices', user);

  const [activeTab, setActiveTab] = useState('public'); // 'public' | 'e_dawandi'
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useBackButtonClose(showForm, () => { setShowForm(false); setEditItem(null); });

  const emptyForm = {
    title: '',
    content: '',
    category: 'general',
    sub_category: 'निधन वार्ता / शोक संदेश',
    priority: 'normal',
    publish_date: new Date().toISOString().split('T')[0],
    expiry_date: '',
    is_published: true,
    attachment_url: '',
    is_e_dawandi: false,
    broadcast_whatsapp: false,
  };
  const [formData, setFormData] = useState(emptyForm);

  const loadData = useCallback(async () => {
    if (!slug) return;
    setLoading(true);
    try {
      const res = await contentAPI.getNotices(slug);
      setNotices(res.data.notices || []);
    } catch (err) {
      setError('लोड करण्यात अयशस्वी');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenCreate = () => {
    setEditItem(null);
    setFormData({
      ...emptyForm,
      is_e_dawandi: activeTab === 'e_dawandi',
      priority: activeTab === 'e_dawandi' ? 'urgent' : 'normal',
      sub_category: 'निधन वार्ता / शोक संदेश',
      broadcast_whatsapp: activeTab === 'e_dawandi', // default checked for E-Dawandi
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await contentAPI.uploadFile(slug, data);
      const url = res.data?.url || res.data?.fileUrl;
      if (url) {
        setFormData(prev => ({ ...prev, attachment_url: url }));
      } else {
        setError('फाईल अपलोड अयशस्वी: URL सापडला नाही');
      }
    } catch (err) {
      setError('फाईल अपलोड अयशस्वी: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.publish_date && formData.expiry_date && formData.publish_date > formData.expiry_date) {
      setError('मुदत संपण्याची तारीख प्रकाशन तारखेपूर्वी असू शकत नाही.');
      return;
    }
    try {
      const payload = buildNoticePayload(formData);
      if (editItem) {
        await contentAPI.updateNotice(slug, editItem.id, payload);
        setSuccess(formData.is_e_dawandi ? 'ई-दवंडी अद्ययावत केली!' : 'सूचना अद्ययावत केली!');
      } else {
        await contentAPI.createNotice(slug, payload);
        setSuccess(
          formData.is_e_dawandi
            ? (formData.broadcast_whatsapp ? 'ई-दवंडी तयार केली आणि WhatsApp वर पाठवली जात आहे!' : 'ई-दवंडी तयार केली!')
            : (formData.broadcast_whatsapp ? 'सूचना तयार केली आणि WhatsApp वर पाठवली जात आहे!' : 'सूचना तयार केली!')
        );
      }
      setShowForm(false);
      setEditItem(null);
      setFormData(emptyForm);
      loadData();
    } catch (err) {
      setError(apiErrorMessage(err, 'अयशस्वी'));
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      title: item.title,
      content: item.content,
      category: item.category || 'general',
      sub_category: item.sub_category || 'निधन वार्ता / शोक संदेश',
      priority: item.priority || 'normal',
      publish_date: (item.publish_date || item.published_date)?.split('T')[0] || '',
      expiry_date: item.expiry_date?.split('T')[0] || '',
      is_published: item.is_published !== false && item.is_active !== false,
      attachment_url: item.attachment_url || '',
      is_e_dawandi: Boolean(item.is_e_dawandi),
      broadcast_whatsapp: false, // User can explicitly opt-in to re-broadcast on edit
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ही नोंद हटवायची आहे का?')) return;
    try {
      await contentAPI.deleteNotice(slug, id);
      setSuccess('नोंद हटवली');
      loadData();
    } catch (err) {
      setError('हटवण्यात अयशस्वी');
    }
  };

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [publishFilter, setPublishFilter] = useState('');
  const [sort, setSort] = useState({ key: 'publish_date', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const publicCount = useMemo(() => notices.filter(n => !n.is_e_dawandi).length, [notices]);
  const eDawandiCount = useMemo(() => notices.filter(n => Boolean(n.is_e_dawandi)).length, [notices]);

  const filteredNotices = useMemo(() => {
    return notices.filter(n => {
      const matchTab = activeTab === 'e_dawandi' ? Boolean(n.is_e_dawandi) : !n.is_e_dawandi;
      if (!matchTab) return false;
      if (search && !n.title.toLowerCase().includes(search.toLowerCase())) return false;
      if (categoryFilter && n.category !== categoryFilter) return false;
      if (priorityFilter && n.priority !== priorityFilter) return false;
      if (publishFilter) {
        const isPub = n.is_published !== false && n.is_active !== false;
        if ((publishFilter === 'published') !== isPub) return false;
      }
      return true;
    }).sort((a, b) => {
      const dateKeys = new Set(['publish_date', 'expiry_date']);
      const left = dateKeys.has(sort.key) ? new Date(a[sort.key] || a.published_date || 0).getTime() : String(a[sort.key] ?? '').toLowerCase();
      const right = dateKeys.has(sort.key) ? new Date(b[sort.key] || b.published_date || 0).getTime() : String(b[sort.key] ?? '').toLowerCase();
      return (left > right ? 1 : left < right ? -1 : 0) * (sort.direction === 'asc' ? 1 : -1);
    });
  }, [notices, activeTab, search, categoryFilter, priorityFilter, publishFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filteredNotices.length / pageSize));
  const visibleNotices = filteredNotices.slice((page - 1) * pageSize, page * pageSize);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  const changeSort = key => {
    setSort(current => ({ key, direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc' }));
    setPage(1);
  };

  const SortHeader = ({ column, children }) => (
    <button className={`notice-sort-header ${sort.key === column ? 'active' : ''}`} onClick={() => changeSort(column)}>
      {children}
      <span>{sort.key === column ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
    </button>
  );

  const exportCsv = () => {
    const cell = value => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = [
      ['Title', 'Category', 'Sub Category', 'Priority', 'Publish date', 'Expiry date', 'Published', 'Attachment'],
      ...filteredNotices.map(n => [
        n.title,
        n.category,
        n.sub_category || '',
        n.priority,
        n.publish_date || n.published_date,
        n.expiry_date,
        n.is_published !== false && n.is_active !== false ? 'Yes' : 'No',
        n.attachment_url || '',
      ])
    ];
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob(['\ufeff', rows.map(row => row.map(cell).join(',')).join('\n')], { type: 'text/csv;charset=utf-8' }));
    link.download = `${activeTab === 'e_dawandi' ? 'e-dawandi' : 'notices'}-${new Date().toISOString().slice(0,10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <div>
      <div className="admin-card-header">
        <div>
          <h2>{activeTab === 'e_dawandi' ? '📢 ई-दवंडी (WhatsApp ब्रॉडकास्ट)' : '📋 सार्वजनिक सूचना फलक'}</h2>
          <small style={{ color: '#64748b' }}>
            {activeTab === 'e_dawandi'
              ? 'निधन वार्ता, आपत्कालीन व त्वरित संदेश थेट नागरिकांच्या WhatsApp वर पाठवा (वेबसाइटवर दिसणार नाही)'
              : 'सार्वजनिक वेबसाइटवरील अधिकृत सूचना व परिपत्रके'}
          </small>
        </div>
        {canWriteThis && (
          <button className="btn btn-primary" onClick={showForm ? () => setShowForm(false) : handleOpenCreate}>
            {showForm ? 'रद्द करा' : (activeTab === 'e_dawandi' ? '+ नवीन ई-दवंडी' : '+ नवीन सूचना')}
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="notice-tabs">
        <button
          className={`notice-tab-btn ${activeTab === 'public' ? 'active' : ''}`}
          onClick={() => { setActiveTab('public'); setShowForm(false); setPage(1); }}
        >
          📋 सार्वजनिक सूचना <span className="notice-tab-badge">{publicCount}</span>
        </button>
        <button
          className={`notice-tab-btn ${activeTab === 'e_dawandi' ? 'active' : ''}`}
          onClick={() => { setActiveTab('e_dawandi'); setShowForm(false); setPage(1); }}
        >
          📢 ई-दवंडी (WhatsApp Only) <span className="notice-tab-badge">{eDawandiCount}</span>
        </button>
      </div>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {showForm && (
        <div className="admin-card">
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>
            {editItem
              ? (formData.is_e_dawandi ? 'ई-दवंडी संपादन' : 'सूचना संपादन')
              : (formData.is_e_dawandi ? 'नवीन ई-दवंडी पाठवा' : 'नवीन सूचना तयार करा')}
          </h3>

          {formData.is_e_dawandi && (
            <div className="e-dawandi-banner">
              🔒 <strong>गोपनीयता सूचना:</strong> ही ई-दवंडी केवळ WhatsApp द्वारे नागरिकांना पाठवली जाईल. ती ग्रामपंचायतीच्या सार्वजनिक वेबसाइटच्या सूचना फलकावर <strong>दिसणार नाही</strong>.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>शीर्षक *</label>
                <input
                  value={formData.title}
                  placeholder={formData.is_e_dawandi ? 'उदा. अत्यंत दुःखद निधन वार्ता - कै. ...' : 'उदा. ग्रामपंचायत विशेष सूचना...'}
                  onChange={e => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>

              {formData.is_e_dawandi ? (
                <div className="form-group">
                  <label>ई-दवंडी प्रकार (उपवर्ग) *</label>
                  <select
                    value={formData.sub_category}
                    onChange={e => setFormData({...formData, sub_category: e.target.value})}
                    required
                  >
                    {E_DAWANDI_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>वर्ग</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                  >
                    <option value="general">सामान्य</option>
                    <option value="meeting">बैठक</option>
                    <option value="scheme">योजना</option>
                    <option value="tax">कर</option>
                    <option value="event">कार्यक्रम</option>
                    <option value="tender">निविदा</option>
                  </select>
                </div>
              )}

              <div className="form-group">
                <label>प्राधान्य</label>
                <select
                  value={formData.priority}
                  onChange={e => setFormData({...formData, priority: e.target.value})}
                >
                  <option value="normal">सामान्य</option>
                  <option value="important">महत्त्वाचे</option>
                  <option value="urgent">तातडीचे / अत्यंत महत्त्वाचे</option>
                </select>
              </div>

              <div className="form-group">
                <label>तारीख / प्रकाशन तारीख</label>
                <input
                  type="date"
                  max={formData.expiry_date || undefined}
                  value={formData.publish_date}
                  onChange={e => setFormData({...formData, publish_date: e.target.value})}
                />
              </div>

              {!formData.is_e_dawandi && (
                <div className="form-group">
                  <label>मुदत संपण्याची तारीख</label>
                  <input
                    type="date"
                    min={formData.publish_date || undefined}
                    value={formData.expiry_date}
                    onChange={e => setFormData({...formData, expiry_date: e.target.value})}
                  />
                </div>
              )}

              {/* Attachment upload */}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>📎 संलग्नीत फाईल (PDF किंवा फोटो/प्रतिमा - ऐच्छिक)</label>
                <div className="attachment-upload-box">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={handleFileUpload}
                      disabled={uploading}
                    />
                    {uploading && <span style={{ fontSize: 12, color: '#0284c7' }}>⏳ फाईल अपलोड होत आहे...</span>}
                    {formData.attachment_url && (
                      <div className="attachment-preview-badge">
                        <span>✓ फाईल जोडली:</span>
                        <a href={formData.attachment_url} target="_blank" rel="noreferrer" style={{ fontWeight: 600, color: '#0369a1' }}>
                          पहा / डाऊनलोड
                        </a>
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, attachment_url: '' })}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontWeight: 'bold' }}
                          title="काढून टाका"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>
                    * PDF परिपत्रक किंवा माहितीचा फोटो जोडू शकता. WhatsApp वर नागरिकांना हा फोटो/PDF थेट पाठवला जाईल.
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label>मजकूर / संदेश *</label>
                <textarea
                  rows="5"
                  value={formData.content}
                  placeholder={
                    formData.is_e_dawandi
                      ? 'नागरिकांसाठी संपूर्ण संदेश येथे लिहा (उदा. अंत्यविधीची वेळ, स्थळ किंवा दुरुस्तीची माहिती...)'
                      : 'सूचनेचा संपूर्ण तपशील...'
                  }
                  onChange={e => setFormData({...formData, content: e.target.value})}
                  required
                />
              </div>

              {/* WhatsApp broadcast checkbox */}
              <div className="form-group" style={{ gridColumn: 'span 2', background: '#f0fdf4', padding: '12px 14px', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 600, color: '#166534', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={formData.broadcast_whatsapp}
                    onChange={e => setFormData({ ...formData, broadcast_whatsapp: e.target.checked })}
                    style={{ width: 18, height: 18 }}
                  />
                  <span>
                    📱 नोंदणीकृत सर्व नागरिकांना WhatsApp वर पाठवा
                    {formData.is_e_dawandi && ' (ई-दवंडीसाठी शिफारस केलेले)'}
                  </span>
                </label>
                <div style={{ fontSize: 11, color: '#15803d', marginTop: 4, marginLeft: 28 }}>
                  {formData.attachment_url
                    ? '✓ संलग्नीत फाईलसह (PDF/Image) WhatsApp वर ब्रॉडकास्ट टेम्पलेट पाठवले जाईल.'
                    : '✓ फक्त मजकूर स्वरूपात WhatsApp वर ब्रॉडकास्ट टेम्पलेट पाठवले जाईल.'}
                </div>
              </div>
            </div>

            <div className="admin-form-actions" style={{ marginTop: 20 }}>
              <button type="submit" className="btn btn-primary" disabled={uploading}>
                {editItem ? 'अद्ययावत करा' : (formData.is_e_dawandi ? '📢 ई-दवंडी पाठवा' : 'सूचना तयार करा')}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => { setShowForm(false); setEditItem(null); setFormData(emptyForm); }}
              >
                रद्द करा
              </button>
            </div>
          </form>
        </div>
      )}

      {!showForm && (
        <div className="admin-card notice-table-card">
          <div className="notice-table-toolbar">
            <label className="notice-search">
              <span>⌕</span>
              <input
                placeholder="शीर्षकाने शोधा..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </label>

            {activeTab === 'public' ? (
              <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व वर्ग</option>
                <option value="general">सामान्य</option>
                <option value="meeting">बैठक</option>
                <option value="scheme">योजना</option>
                <option value="tax">कर</option>
                <option value="event">कार्यक्रम</option>
                <option value="tender">निविदा</option>
              </select>
            ) : null}

            <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}>
              <option value="">सर्व प्राधान्य</option>
              <option value="normal">सामान्य</option>
              <option value="important">महत्त्वाचे</option>
              <option value="urgent">तातडीचे</option>
            </select>

            {activeTab === 'public' && (
              <select value={publishFilter} onChange={e => { setPublishFilter(e.target.value); setPage(1); }}>
                <option value="">सर्व प्रकाशन</option>
                <option value="published">प्रकाशित</option>
                <option value="draft">अप्रकाशित</option>
              </select>
            )}

            <button className="notice-export-btn" onClick={exportCsv}>⇩ CSV निर्यात</button>
          </div>

          <div className="notice-table-meta">
            <span>
              <strong>{filteredNotices.length}</strong> {activeTab === 'e_dawandi' ? 'ई-दवंडी नोंदी' : 'सूचना नोंदी'}
            </span>
            <span className={`notice-load-status ${loading ? 'visible' : ''}`}>
              <i /> माहिती अद्ययावत होत आहे
            </span>
          </div>

          <div className={`table-scroll notice-table-scroll ${loading ? 'is-refreshing' : ''}`}>
            <table className="data-table notice-data-table">
              <thead>
                <tr>
                  <th><SortHeader column="title">शीर्षक</SortHeader></th>
                  <th>{activeTab === 'e_dawandi' ? 'प्रकार / उपवर्ग' : <SortHeader column="category">वर्ग</SortHeader>}</th>
                  <th><SortHeader column="priority">प्राधान्य</SortHeader></th>
                  <th><SortHeader column="publish_date">तारीख</SortHeader></th>
                  <th>संलग्नीत फाईल</th>
                  <th>{activeTab === 'e_dawandi' ? 'प्रसारण' : <SortHeader column="is_published">स्थिती</SortHeader>}</th>
                  <th>कृती</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <LoadingSpinner.TableRow colSpan={7} message={activeTab === 'e_dawandi' ? 'ई-दवंडी लोड होत आहे...' : 'सूचना लोड होत आहेत...'} />
                ) : visibleNotices.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="notice-empty">
                      {activeTab === 'e_dawandi' ? 'ई-दवंडी नोंदी आढळल्या नाहीत.' : 'सूचना आढळल्या नाहीत.'}
                    </td>
                  </tr>
                ) : (
                  visibleNotices.map((n) => (
                  <tr key={n.id}>
                    <td>
                      <div className="notice-title-cell">
                        <strong>{n.title}</strong>
                        <small>{String(n.content || '').replace(/<[^>]+>/g, '').slice(0, 80)}</small>
                      </div>
                    </td>
                    <td>
                      <span className="notice-category">
                        {activeTab === 'e_dawandi' ? (n.sub_category || 'ई-दवंडी') : n.category}
                      </span>
                    </td>
                    <td>
                      <span className={`notice-priority priority-${n.priority}`}>{n.priority}</span>
                    </td>
                    <td className="notice-date">
                      {(n.publish_date || n.published_date) ? formatMarathiDate(n.publish_date || n.published_date) : '-'}
                    </td>
                    <td>
                      {n.attachment_url ? (
                        <a
                          href={n.attachment_url}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: 11, color: '#0284c7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          📎 <span>फाईल पहा</span>
                        </a>
                      ) : (
                        <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>
                      )}
                    </td>
                    <td>
                      {activeTab === 'e_dawandi' ? (
                        <span className="whatsapp-badge">📢 WhatsApp</span>
                      ) : (
                        <span className={`notice-publish ${n.is_published !== false && n.is_active !== false ? 'published' : 'draft'}`}>
                          {n.is_published !== false && n.is_active !== false ? 'प्रकाशित' : 'अप्रकाशित'}
                        </span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {canWriteThis && (
                          <button className="notice-edit-btn" onClick={() => handleEdit(n)}>संपादन</button>
                        )}
                        {canWriteThis && (
                          <button className="notice-delete-btn" onClick={() => handleDelete(n.id)}>हटवा</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
              </tbody>
            </table>
          </div>

          <div className="table-pagination notice-pagination">
            <label>
              प्रति पृष्ठ{' '}
              <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option>10</option>
                <option>20</option>
                <option>50</option>
              </select>
            </label>
            <button className="btn btn-outline btn-sm" disabled={page === 1} onClick={() => setPage(value => Math.max(1, value - 1))}>
              ← मागील
            </button>
            <span>पृष्ठ <strong>{page}</strong> / {totalPages}</span>
            <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>
              पुढील →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
